/**
 * Screen 2 of 4 — INTAKE.
 *
 * Collects the run inputs and posts them to
 * `POST /v1/valuation/engagements/{id}/recompute` (VAL-INDUSTRY-WIRING-1 R6C
 * — the same signed engine Tuning and Sign-off run on). Every number typed
 * here is an INPUT; nothing on this screen computes a value. The engine
 * composes the band on the signed methodology and the browser renders what
 * came back.
 *
 * Unit convention, inherited from `core/valuation` and not re-interpreted
 * here: rates are PERCENT-SCALE (4.35 means 4.35%), weights are fractions.
 *
 * Optional legs are genuinely optional — leaving the asset floor or the driver
 * scores blank drops that leg rather than substituting a guess. Comps are
 * optional in a subtler way: triangulation always needs a market leg, but with
 * the box empty the signed industry profile supplies one from its own band, so
 * blank is the normal path and typing comps is the override.
 */
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  fetchValuationMethodology,
  getValuationEngagement,
  updateValuationEngagement,
} from '@/api/valuation'
import { recomputeValuation } from '@/api/valuationB3'
import { formatApiError } from '@/utils/httpErrors'
import type {
  ValuationEngagement,
  ValuationMethodology,
} from '@/types/valuation'
import { IntakeMultipleOverride } from './_IntakeMultipleOverride'
import { DriverQuestionnaireSection } from './_DriverQuestionnaire'
import {
  BLANK_PARTICULARS,
  IntakeParticularsSection,
  particularsFromEngagement,
  type ParticularsState,
} from './_IntakeParticulars'
import {
  ValuationStepper,
  ValueKindBadge,
  nextStepPath,
  runCache,
  useValuationEntitlement,
} from './_shared'
import {
  buildIntakeBody,
  buildIntakeRecomputeBody,
  getMissingRequiredFields,
  intakeAutosavePatch,
} from './_valuationIntakeHelpers'
import { benchmarkKeyForIndustry } from './_valuationIndustryMap'
import { useValuationDraft } from './_valuationDraft'
import { useIntakeInputs, intakeDraftPatch } from './_useIntakeInputs'
import { useResumeScroll } from './_useResumeScroll'
import { useAutosave } from './_useAutosave'
import { IntakeMissingFields } from './_IntakeMissingFields'
import { IntakeLegsSection } from './_IntakeLegs'

/** FIX_VAL-INTAKE-NOTES-1 — the server-written note under a pre-filled field. */
function sourceNote(d?: { note: string } | null): string | undefined {
  return d?.note
}

export default function ValuationIntake() {
  const { valuationId } = useParams<{ valuationId: string }>()
  const navigate = useNavigate()
  const { canOverride } = useValuationEntitlement()

  const [engagement, setEngagement] = useState<ValuationEngagement | null>(null)
  const [methodology, setMethodology] = useState<ValuationMethodology | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [running, setRunning] = useState(false)

  // VAL-INTAKE-1 D2/D3/D4, corrected by VAL-NAV-STATE-1 R3 — the one working
  // draft, shared with Tuning. Bootstrap-save rationale: see `intakeDraftPatch`.
  const {
    draft,
    draftExtras,
    resuming,
    resumedAtLabel,
    loading: draftLoading,
    save: saveDraft,
    draftTuning,
    dismiss: dismissDraft,
  } = useValuationDraft(valuationId)
  const {
    riskFree, setRiskFree,
    equityRiskPremium, setEquityRiskPremium,
    sizePremium, setSizePremium,
    companySpecificRisk, setCompanySpecificRisk,
    longTermGrowth, setLongTermGrowth,
    normalizedEarnings, setNormalizedEarnings,
    baseCashFlow, setBaseCashFlow,
    replacementCompensation, setReplacementCompensation,
    growthRate, setGrowthRate,
    projectionYears, setProjectionYears,
    capWeight, setCapWeight,
    compMultiples, setCompMultiples,
    totalAssets, setTotalAssets,
    totalLiabilities, setTotalLiabilities,
    fairValueAdjustments, setFairValueAdjustments,
    driverAnswers, setDriverAnswers,
    overrideMultiple, setOverrideMultiple,
    overrideRationale, setOverrideRationale,
    overrideDisposition, setOverrideDisposition,
    hydrated,
    typedBeforeHydrate,
    startClean,
  } = useIntakeInputs(draft, draftLoading, dismissDraft, methodology?.input_defaults ?? null)
  const resumeQuestionId = useResumeScroll(methodology?.driver_questionnaire ?? null, driverAnswers, !!methodology && hydrated)
  // VAL-WALK-POLISH-3 P2 — #particulars lands on the particulars card, not the first unanswered question.
  const toParticulars = useLocation().hash === '#particulars'
  const focusQuestionId = toParticulars ? null : resumeQuestionId
  const scrolledToParticulars = useRef(false)
  useEffect(() => {
    if (!toParticulars || scrolledToParticulars.current || !engagement || !methodology) return
    const el = document.getElementById('particulars')
    if (!el) return
    scrolledToParticulars.current = true
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [toParticulars, engagement, methodology])
  const [particulars, setParticulars] = useState<ParticularsState>(BLANK_PARTICULARS)
  const particularsTypedEarly = useRef(false)

  // VAL-SAVE-ON-TYPE-1A — every change is saved ~1.5 s after typing stops
  // (DATA_PROVENANCE_RULE rule 1). Off until the draft has hydrated, so the
  // blank form can never overwrite it; no engine call from here.
  const { patch: autosavePatch, problems: autosaveProblems } = intakeAutosavePatch({
    riskFree, equityRiskPremium, sizePremium, companySpecificRisk, longTermGrowth,
    normalizedEarnings, baseCashFlow, replacementCompensation, growthRate, projectionYears, capWeight,
    compMultiples, totalAssets, totalLiabilities, fairValueAdjustments,
    driverAnswers, overrideMultiple, overrideRationale, overrideDisposition, hasDraft: !!draft,
  })
  const needsEarnings = !draft && autosavePatch.normalized_earnings === undefined
  const draftSave = useAutosave(
    draft ? autosavePatch : { ...autosavePatch, benchmark_key: benchmarkKeyForIndustry(engagement?.industry) },
    saveDraft,
    {
      enabled: hydrated && !!engagement,
      dirty: typedBeforeHydrate,
      blocked: needsEarnings ? 'Not saved — enter normalized earnings to start the draft' : null,
    },
  )
  const particularsSave = useAutosave(
    { ...particulars, effective_date: particulars.effective_date || null, premise_of_value: particulars.premise_of_value.trim() || null },
    async body => {
      if (!valuationId) return null
      try { await updateValuationEngagement(valuationId, body); return null } catch (err: unknown) { return formatApiError(err) }
    },
    { enabled: !!engagement, dirty: particularsTypedEarly.current },
  )
  const saves = [draftSave, particularsSave]
  const failed = saves.find(s => s.status === 'error')
  const saveLine = autosaveProblems.length > 0 ? `Not saved — check ${autosaveProblems.join(', ')}`
    : draftSave.status === 'blocked' ? draftSave.message
    : failed ? `Not saved — ${failed.message}`
    : saves.some(s => s.status === 'saving') ? 'Saving…'
    : saves.find(s => s.status === 'saved')?.message ?? null

  useEffect(() => {
    if (!valuationId) return
    let live = true
    Promise.all([getValuationEngagement(valuationId), fetchValuationMethodology()])
      .then(([en, meth]) => {
        if (!live) return
        setEngagement(en)
        setMethodology(meth)
        // VAL-WALK-FIX-1 — fields typed before this GET resolved win over the server copy.
        const fromServer = particularsFromEngagement(en)
        setParticulars(prev => {
          const typed = Object.fromEntries(Object.entries(prev).filter(([k, v]) => v !== BLANK_PARTICULARS[k as keyof ParticularsState]))
          particularsTypedEarly.current = Object.keys(typed).length > 0
          return { ...fromServer, ...typed }
        })
      })
      .catch((err: unknown) => { if (live) setLoadError(formatApiError(err)) })
    return () => { live = false }
  }, [valuationId])

  async function handleRun(e: React.FormEvent) {
    e.preventDefault()
    if (!valuationId) return
    if (missingFields.length > 0) return
    // VAL-INPUT-PERSIST-1D BUILD SHEET item 3 — a multiple entered with no
    // reason cannot be saved or run; the reason is what makes an override
    // attributable, not decorative.
    if (overrideMultiple.trim() !== '' && overrideRationale.trim() === '') {
      toast.error('The multiple override needs a reason.')
      return
    }
    const body = buildIntakeBody({
      riskFree, equityRiskPremium, sizePremium, companySpecificRisk, longTermGrowth,
      normalizedEarnings, replacementCompensation, baseCashFlow, growthRate, projectionYears, capWeight,
      compMultiples, totalAssets, totalLiabilities, fairValueAdjustments,
      driverAnswers, questions: methodology?.driver_questionnaire.questions ?? [],
    })
    if (typeof body === 'string') { toast.error(body); return }

    // D2, corrected by R3 — presses Run. Unconditional pre-run save (bootstrap
    // pair added when no draft exists yet — see `intakeDraftPatch`); a save
    // that cannot persist is a visible error, not a silent no-op.
    const preRunSaveError = await saveDraft(
      intakeDraftPatch(body, draft, benchmarkKeyForIndustry(engagement?.industry), {
        overrideMultiple, overrideRationale, overrideDisposition,
      }),
    )
    if (preRunSaveError) toast.error(preRunSaveError)
    updateValuationEngagement(valuationId, { ...particulars, effective_date: particulars.effective_date || null, premise_of_value: particulars.premise_of_value.trim() || null })
      .catch((err: unknown) => toast.error(formatApiError(err)))

    // VAL-INDUSTRY-WIRING-1 R6C — same signed engine as Tuning/Sign-off
    // (`/recompute`, not `/run`); merge-and-build shared with Tuning.
    const recomputeBody = buildIntakeRecomputeBody({
      body,
      driverAnswers,
      draft,
      draftTuning,
      industryKey: benchmarkKeyForIndustry(engagement?.industry),
      overrideMultiple, overrideRationale, overrideDisposition,
    })
    if (!recomputeBody) {
      toast.error('Missing industry or earnings information — check the required fields above.')
      return
    }

    setRunning(true)
    try {
      const result = await recomputeValuation(valuationId, recomputeBody)
      // /recompute does not persist anything more than /run did — the band,
      // the provenance, the multiple, the overrides all live only in this
      // response and nowhere else, so cache it for the output screen. See
      // _shared.tsx.
      runCache.save(valuationId, result)
      // D6 — the draft's `income_value` is not a form field on this screen,
      // it's this screen's OWN computed DCF result: the only defensible
      // income-approach dollar figure to hand Tuning is the one the signed
      // methodology just composed, not a number typed a second time. Saved
      // unconditionally, same reasoning as the pre-run save above.
      const postRunSaveError = await saveDraft({ income_value: result.income_value })
      if (postRunSaveError) toast.error(postRunSaveError)
      toast.success('Valuation run complete.')
      // R3.7 — follow the rail (profile → intake → tuning → output →
      // signoff), not a hardcoded `/output`: a fully-run Intake used to land
      // on Output directly, so Tuning — the step where the draft's
      // `benchmark_key` actually gets exercised against the real benchmark
      // table — was never visited on the straight-through path.
      const next = nextStepPath('intake', valuationId)
      if (next) navigate(next)
    } catch (err: unknown) {
      toast.error(formatApiError(err))
    } finally {
      setRunning(false)
    }
  }

  if (loadError) {
    return (
      <div className="mx-auto max-w-7xl p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{loadError}</div>
      </div>
    )
  }
  if (!engagement || !methodology) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 p-6">
        <div>
          <Skeleton className="h-8 w-64" />
          <Skeleton className="mt-2 h-4 w-40" />
        </div>
        <Skeleton className="h-10 w-full" />
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i}>
            <CardHeader><Skeleton className="h-5 w-32" /></CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  const crossover = methodology.earnings_basis_rule.crossover_earnings_threshold
  const startingNote = (key: string, current: string): string | undefined => {
    if (key === 'risk_free_rate') return sourceNote(methodology?.risk_free_reference ?? methodology?.input_defaults?.[key])
    const d = methodology?.input_defaults?.[key]
    return d && current === String(d.value) ? sourceNote(d) : undefined
  }
  const missingFields = getMissingRequiredFields({ riskFree, companySpecificRisk, longTermGrowth, normalizedEarnings, growthRate, replacementCompensation, crossover, projectionYears, capWeight })

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{engagement.subject_company_name}</h1>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-500">
          <ValueKindBadge kind={engagement.value_kind} />
          <span className="text-xs">{engagement.industry_label ?? engagement.industry ?? 'no industry set'}</span>
          {engagement.period_label && <span>· {engagement.period_label}</span>}
        </div>
        {resuming && (
          <p className="mt-2 flex flex-wrap items-center gap-2 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
            <span>Resuming your draft from {resumedAtLabel}.</span>
            <button
              type="button"
              onClick={startClean}
              className="font-medium underline underline-offset-2 hover:text-blue-700"
            >
              Start clean
            </button>
          </p>
        )}
        {saveLine && (
          <p className="mt-2 text-xs text-gray-500" role="status">{saveLine}</p>
        )}
      </div>

      <ValuationStepper current="intake" engagementId={valuationId} />

      <form onSubmit={handleRun} className="space-y-6">
        <IntakeParticularsSection particulars={particulars} setParticulars={setParticulars} />

        <IntakeLegsSection
          methodology={methodology}
          industry={engagement.industry}
          normalizedEarnings={normalizedEarnings} setNormalizedEarnings={setNormalizedEarnings}
          replacementCompensation={replacementCompensation} setReplacementCompensation={setReplacementCompensation}
          baseCashFlow={baseCashFlow} setBaseCashFlow={setBaseCashFlow}
          riskFree={riskFree} setRiskFree={setRiskFree}
          startingNote={startingNote}
          companySpecificRisk={companySpecificRisk} setCompanySpecificRisk={setCompanySpecificRisk}
          equityRiskPremium={equityRiskPremium} setEquityRiskPremium={setEquityRiskPremium}
          sizePremium={sizePremium} setSizePremium={setSizePremium}
          longTermGrowth={longTermGrowth} setLongTermGrowth={setLongTermGrowth}
          growthRate={growthRate} setGrowthRate={setGrowthRate}
          projectionYears={projectionYears} setProjectionYears={setProjectionYears}
          capWeight={capWeight} setCapWeight={setCapWeight}
          compMultiples={compMultiples} setCompMultiples={setCompMultiples}
          totalAssets={totalAssets} setTotalAssets={setTotalAssets}
          totalLiabilities={totalLiabilities} setTotalLiabilities={setTotalLiabilities}
          fairValueAdjustments={fairValueAdjustments} setFairValueAdjustments={setFairValueAdjustments}
        />

        <DriverQuestionnaireSection
          questionnaire={methodology.driver_questionnaire}
          answers={driverAnswers}
          onAnswer={(questionId, value) =>
            setDriverAnswers(prev => ({ ...prev, [questionId]: value }))
          }
          persist={{ valuationId, saved: draftExtras.deferred_questions ?? undefined, enabled: !!draft }}
          focusQuestionId={focusQuestionId}
        />

        {canOverride && (
          <IntakeMultipleOverride
            overrideMultiple={overrideMultiple} setOverrideMultiple={setOverrideMultiple}
            overrideRationale={overrideRationale} setOverrideRationale={setOverrideRationale}
            overrideDisposition={overrideDisposition} setOverrideDisposition={setOverrideDisposition}
          />
        )}

        <IntakeMissingFields fields={missingFields} />
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/valuation')}>
            Back
          </Button>
          <Button type="submit" disabled={running}>
            {running ? 'Running…' : 'Run valuation'}
          </Button>
        </div>
      </form>
    </div>
  )
}
